import { Section, Sub, Card, Note, Warn, Fields, New, Shot } from '../../../components/docs/Blocks';

export default function PlayerEditor() {
  return (
    <Section
      id="player-editor"
      title="Player Editor"
      lead="Everyone who has ever joined the world, and everything stored about them: where they are, what they are carrying, what effects they have, what they have tamed, and the raw data behind all of it."
    >
      <Sub id="player-choose" title="Choosing a player">
        <Card>
          <p>
            The Player Editor works on one player at a time, so a picker sits above
            the screen list. Until you choose someone, the screens below it are greyed
            out and the panel explains why — the section has a shape you can see
            before it has data to show.
          </p>
          <Fields
            items={[
              ['The picker', 'Every player record in the world, searchable. The host of the world is the Local Player; everyone else is a remote player.'],
              ['Names', 'Bedrock does not store gamertags in player records, so most worlds have no names in them at all. Toolchest resolves a name from three places, in order: a dynamic property written by the PlayerName add-on, a name you typed yourself, and a PlayerName tag on the record.'],
              ['Unsaved marker', 'The picker flags a player who has staged changes, so switching between people does not lose track of who you have edited.'],
            ]}
          />
          <Note>
            A name you type is stored beside the world, not inside it, so it never
            changes anything Minecraft reads. If the name you type is a real gamertag,
            Toolchest fetches that player’s head and 3D model to show alongside — which
            you can turn off entirely in Settings.
          </Note>
        </Card>
      </Sub>

      <Sub id="player-general" title="General">
        <Shot
          name="player-player.general"
          alt="The General screen showing a player's identity, location, survival stats and experience"
        />
        <Card>
          <Fields
            items={[
              ['Identity', 'The display name, the record key the player is filed under, and their Microsoft account where the world records one. The head and 3D model beside them are fetched for the name; if the player has changed their skin, Refresh head and model fetches them again rather than showing the cached one.'],
              ['Location', 'Position X, Y and Z, the dimension they are in, and which way they are facing as yaw and pitch. Show on World Map switches to the map, frames the player and marks them; clicking their head on the map comes back here.'],
              ['Survival', 'Health (20 is ten hearts), food level, food saturation — the hidden hunger buffer — exhaustion, and air (300 is a full breath).'],
              ['Experience', 'Experience level, and progress through the current level as a fraction from 0 to 1.'],
              ['Game State', "The player's own game mode, which can differ from the world default, and when they last died."],
              ['Remove player', 'Deletes the record and every pointer to it when you save.'],
            ]}
          />
          <Warn>
            Removing the host is different from removing anyone else: Minecraft simply
            makes a new host record next time the world loads, so they come back as a
            brand new player with a spawn inventory, no position and no progress.
            Toolchest says so on the button rather than after the fact.
          </Warn>
        </Card>
      </Sub>

      <Sub id="player-inventory" title="Inventory">
        <Shot
          name="player-player.inventory"
          alt="The Inventory screen showing hotbar, main inventory, armour, offhand and ender chest with an item selected"
        />
        <Card>
          <p>
            Every container a player carries, laid out as Minecraft shows them:
            hotbar, main inventory, armour, offhand and ender chest. The counts along
            the top say how full each one is.
          </p>
          <Fields
            items={[
              ['Moving items', 'Drag a stack from one slot to another. A slot that cannot hold that item — a sword in an armour slot — refuses it and says why rather than silently dropping it.'],
              ['Editing an item', 'Click a slot to open its editor: the item itself with a searchable picker, stack size, damage, a custom anvil name, and the unbreakable flag.'],
              ['Enchantments', 'Add any enchantment at any level. Toolchest tells you the level the game normally allows and lets you go past it, and it flags an enchantment that does not belong on that item rather than removing it.'],
              ['Armour trims', 'Pattern and material on any armour piece.'],
              ['Item variants', 'Potions, tipped arrows, banners, beds, goat horns and suspicious stew get their own variant pickers rather than a raw damage value.'],
              ['Containers', 'Select a shulker box or bundle and its contents appear as slots of their own, to click, drag into and out of, or empty, exactly like the rest of the inventory. It works at any depth, so a bundle inside a shulker box opens too. Bundles show how full they are and will not take more than the game allows.'],
              ['Copy and paste', 'New in 3.0.4. Copy item puts the selected item on the item clipboard, which shows what it holds and whose it was. Paste it into another slot, another player or another world — the copy stays after the world it came from is closed, and until you clear it. The one exception is a filled map, whose picture lives in its own world, so it pastes only within that world.'],
              ['Clear inventory', 'Empties every container at once. Like everything else, staged until you save.'],
            ]}
          />
        </Card>
      </Sub>

      <Sub id="player-custom-item" title={<>Custom Item <New>Since 3.0.1</New></>}>
        <Shot
          name="player-player.item_builder"
          alt="The Custom Item screen building an item with a name, lore and enchantments"
        />
        <Card>
          <p>
            Builds an item from nothing and adds it to the player. Choose any item or
            block as the base, then set whatever the item should carry — the same
            editor as an inventory slot, plus the things an anvil cannot do.
          </p>
          <Fields
            items={[
              ['Name and lore', 'A custom name and as many lore lines as you like.'],
              ['Durability and enchantments', 'Damage, the unbreakable flag, and any enchantment at any level the game will honour — well past what an enchanting table allows, and capped where the game itself stops counting.'],
              ['Keep on death', 'The item stays in the inventory when the player dies.'],
              ['Item lock', 'Lock it in its slot, or anywhere in the inventory, so it cannot be moved or dropped.'],
              ['Adventure mode', 'The blocks the item can be placed on and can destroy, one block id per line, for adventure-mode worlds.'],
              ['Advanced NBT', 'The complete item compound, for anything the form does not cover. Only the NBT rules are enforced; the game may ignore combinations it does not understand.'],
              ['Add to inventory', 'Places a copy in the first empty main-inventory slot and stages it. The draft stays, so the same item can be given again or to somebody else.'],
            ]}
          />
        </Card>
      </Sub>

      <Sub id="player-effects" title="Effects">
        <Shot
          name="player-player.effects"
          alt="The Effects screen with active effects beside the full list of available ones"
        />

        <Card>
          <p>
            The status effects currently on the player. The list on the right is
            everything Minecraft has, searchable and marked as helpful, harmful or
            neither; the list on the left is what this player has now.
          </p>
          <Fields
            items={[
              ['Duration', 'In seconds, up to about 277 hours — or Forever, which Bedrock stores as a duration of -1.'],
              ['Level', 'The amplifier, from 1 upwards. Level 1 is amplifier 0; Bedrock allows up to 256.'],
              ['Show particles', 'Turning this off hides the visual swirl but keeps the effect working.'],
            ]}
          />
        </Card>
      </Sub>

      <Sub id="player-tamed" title={<>Tamed Pets <New /></>}>
        <Shot
          name="player-player.tamed"
          alt="The Tamed Pets screen listing animals tamed to the selected player"
        />

        <Card>
          <p>
            Every animal in the world tamed to this player — wolves, cats, horses,
            parrots and llamas. Taming writes the player’s id onto the animal, and
            that is what this list reads, so it finds pets anywhere in the world
            rather than only nearby ones.
          </p>
          <Fields
            items={[
              ['The list', 'Each pet with its type, name, where it is and whether it is sitting. The counts also tell you how many animals are tamed to other players, and how many are tamed but have no owner at all.'],
              ['Release', 'Removes the ownership so the animal becomes wild again.'],
              ['Reassign', 'Hands the pet to another player in the world.'],
              ['Copy', 'New in 3.1. Puts the pet on the entity clipboard, to paste beside a player in this world or another — where it becomes that player’s pet. Paste it from the Entities screen.'],
              ['Open in Entities', 'Jumps to that animal in the Entities screen for a full edit.'],
            ]}
          />
          <Note>
            A player whose record carries no UniqueID cannot own anything — there is no
            id for an animal to point at. Toolchest says so rather than showing an
            empty list.
          </Note>
        </Card>
      </Sub>

      <Sub id="player-properties" title="Properties and tags">
        <Card>
          <p>
            The custom data add-ons store on a player, and the script tags used to
            group players.
          </p>
          <Fields
            items={[
              ['Dynamic properties', 'Grouped by the add-on that set them, with the add-on resolved to its readable name where Toolchest can find it — from the packs in the world, the world\'s own pack history, or the installed add-on library. Text, number and true-or-false values can be edited in place.'],
              ['Remove all', 'Clears every property one add-on set on this player, which is the tidy way to undo a pack you have removed.'],
              ['Player tags', 'Add and remove the tags commands and add-ons test against, such as admin or builder.'],
              ['Filter', 'Searches keys and values, which matters on a player carrying hundreds of properties.'],
            ]}
          />
          <p>
            A property whose shape Toolchest does not recognise is shown but not
            edited here. The Advanced screen can still reach it.
          </p>
        </Card>
      </Sub>

      <Sub id="player-advanced" title="Advanced">
        <Card>
          <p>
            The player’s raw NBT as a tree, the same editor the Level Editor’s
            Advanced screen uses. This is where abilities, attributes, recipe unlocks
            and anything else without a dedicated screen live.
          </p>
          <Note>
            Unlike level.dat, list entries here <em>are</em> editable — attributes,
            position and tags among them — because a player record’s lists have stable
            addresses that level.dat’s do not.
          </Note>
          <Warn>
            No checks beyond each tag’s own type, and it overrides the other player
            screens. Nothing is written until you save, and a backup is taken first.
          </Warn>
        </Card>
      </Sub>

      <Sub id="player-search" title="Searching across players">
        <Card>
          <p>
            <strong>Search</strong>, above the screen list, looks through every player
            in the world at once — not just the one you are editing. It finds items
            (including items inside containers), tags and dynamic properties, and
            selecting a result takes you to that player and that screen.
          </p>
          <p>
            Useful for questions the per-player screens cannot answer: who has the
            elytra, who is carrying the admin tool, which player an add-on has flagged.
          </p>
        </Card>
      </Sub>

      <Sub id="player-transfer" title={<>Player transfer <New /></>}>
        <Card>
          <p>
            <strong>Transfer</strong> moves player data around. Three operations share
            one panel, and all three let you choose exactly which parts move.
          </p>
          <Fields
            items={[
              ['Export player', 'Writes the chosen sections to a file you can keep or send.'],
              ['Import player', 'Reads one of those files into the player you have selected. Sections you did not choose are left as they are.'],
              ['Copy from another player', 'Copies chosen sections from someone else in the same world. The destination keeps its own account identity, and there is an option to remove the source afterwards.'],
            ]}
          />
          <p>The sections are:</p>
          <Fields
            items={[
              ['General and progress', 'Position, spawn, health, experience, game mode and general progress.'],
              ['Inventory', 'Inventory, equipment, ender chest, held items and the selected slot.'],
              ['Effects', 'Every active effect with its duration and fade state.'],
              ['Properties and tags', 'Dynamic properties and script tags from add-ons.'],
              ['Advanced / other state', 'Abilities, recipes, UI state and every unclassified field. Deliberately broad — leave it off for a narrow transfer, and note that Everything includes it so nothing unknown is lost.'],
            ]}
          />
          <Note>
            <strong>Update related world references</strong> moves the world-level
            references that name a player, where the source can be verified in the
            world being edited. Toolchest reports how many exact references it will
            move and how many are ambiguous — the ambiguous ones are reported and left
            alone rather than guessed at.
          </Note>
          <p>
            A transfer is staged like every other change. It appears in the review
            sheet and is only written when you save.
          </p>
        </Card>
      </Sub>
    </Section>
  );
}
